/* eslint-disable react/no-unstable-nested-components */
import { ReactNode, useMemo, useState } from 'react';
import ResponsiveTable from '../../../../shared/components/ResponsiveTable';
import {
  FooterColumn,
  SortedColumn,
} from '../../../../shared/components/ResponsiveTable/models';
import SimpleResponsiveCell from '../../../../shared/components/ResponsiveTable/SimpleResponsiveCell';
import {
  estimatedQuantityTitle,
  isPayByUser,
} from '../../../../shared/constants';
import { AgreementApiModel } from '../../../../shared/models/DealModel';
import {
  DiscountType,
  ProductApiModel,
} from '../../../../shared/models/ProductModel';
import formatCurrency from '../../../../shared/utils/formatCurrency';
import getNetQuantity from '../../../../shared/utils/getNetQuantity';
import useDealTotalSpend from '../../useDealTotalSpend';
import styles from './dealProductsTable.module.scss';
import ProductNameCell from './ProductNameCell';
import { dealHasExpired } from '../../../../shared/utils/dataHelpers';

// Single source of truth for column identifiers. These are referenced by the
// column definitions, the footer definitions and the payment-arrangement
// visibility lists, so a typo in any one place would silently break filtering.
const COLUMN = {
  name: 'name',
  priceBase: 'priceBase',
  discount: 'discount',
  quantityTotal: 'quantityTotal',
  assigned: 'assigned',
  quantityAssigned: 'quantityAssigned',
  quantityRedeemed: 'quantityRedeemed',
  totalCost: 'totalCost',
  totalSavings: 'totalSavings',
} as const;

// Shown while the Pay-by-User deal products request is still in flight.
const PENDING_PLACEHOLDER = '—';

// Reusable progress bar row component
function ProgressBarRow({
  value,
  available,
  percentage,
  isPbu,
}: {
  value: number;
  available: ReactNode;
  percentage: number;
  isPbu: boolean;
}) {
  return (
    <div className={styles.productDetailsRow}>
      <div>{value}</div>
      {!isPbu && (
        <div className={`${styles.outer}`}>
          <div style={{ width: `${percentage}%` }} className={styles.inner} />
        </div>
      )}
      <div className={styles.smallText}>{available} Available</div>
    </div>
  );
}

function getNormalizedCount(value: number) {
  return Math.max(0, value);
}

function getClampedPercentage(value: number) {
  return Math.min(100, Math.max(0, Math.round(value)));
}

const getTableColumns = (
  deal: AgreementApiModel,
  getProductSpend: (product: ProductApiModel) => number,
  getProductSavings: (product: ProductApiModel) => number,
  isTotalsPending: boolean,
): SortedColumn[] => {
  const isExpired = dealHasExpired(deal);
  const isDisabled = isExpired || !deal.enabled;
  return [
    {
      name: COLUMN.name,
      title: 'Product',
      cell: (cntx: any) => <ProductNameCell model={cntx.row.original} />,
      width: 23,
      sortingEnabled: true,
      direction: 'Asc',
    },
    {
      name: COLUMN.priceBase,
      title: 'Price',
      cell: (cntx: any) => {
        const hasDiscount = cntx.row.original.discount > 0;

        let label = null;
        switch (cntx.row.original.discountType) {
          case DiscountType.Percentage: {
            label = `${cntx.row.original.discount}% discount`;
            break;
          }
          case DiscountType.Amount: {
            label = `$${cntx.row.original.discount} off`;
            break;
          }
          default: {
            label = '';
          }
        }
        return (
          <div className={styles.productDetailsRow}>
            <div className={hasDiscount ? 'text-decoration-line-through' : ''}>
              ${formatCurrency(cntx.row.original.priceBase)}
            </div>
            {hasDiscount && (
              <>
                <SimpleResponsiveCell label="discount" value={label} />
                <SimpleResponsiveCell
                  label="yourPrice"
                  value={`$${formatCurrency(cntx.row.original.price)}`}
                />
              </>
            )}
          </div>
        );
      },
      width: 10,
    },
    {
      name: COLUMN.discount,
      title: 'Discount',
      cell: (cntx: any) => {
        let label = null;
        switch (cntx.row.original.discountType) {
          case DiscountType.Percentage: {
            label = `${cntx.row.original.discount}%`;
            break;
          }
          case DiscountType.Amount: {
            label = `$${cntx.row.original.discount} off`;
            break;
          }
          default: {
            label = 'No discount';
          }
        }
        return (
          <div className={styles.productDetailsRow}>
            <SimpleResponsiveCell label="discount" value={label} />
          </div>
        );
      },
      width: 6,
    },
    {
      name: COLUMN.quantityTotal,
      title: estimatedQuantityTitle(deal.paymentArrangement),
      cell: (cntx: any) => (
        <SimpleResponsiveCell
          label="Est'd Quantity"
          value={`${getNetQuantity(cntx.row.original)}`}
        />
      ),
      width: 10,
    },
    {
      name: COLUMN.assigned,
      title: 'Assigned',
      cell: (cntx: any) => {
        const payByUser = isPayByUser(deal.paymentArrangement);
        const { quantityRemaining, quantityAssigned } = cntx.row.original;
        const normalizedQuantityNotAssigned =
          getNormalizedCount(quantityRemaining);
        const normalizedQuantityAssigned = getNormalizedCount(quantityAssigned);
        const total =
          normalizedQuantityAssigned + normalizedQuantityNotAssigned;
        const percentage = getClampedPercentage(
          total === 0 ? 0 : (normalizedQuantityNotAssigned / total) * 100,
        );
        let remainingTotal = payByUser ? '∞' : quantityRemaining;
        if (isDisabled) {
          remainingTotal = 0;
        }
        return (
          <ProgressBarRow
            isPbu={payByUser}
            value={quantityAssigned}
            available={remainingTotal}
            percentage={percentage}
          />
        );
      },
      width: 15,
    },
    {
      name: COLUMN.quantityRedeemed,
      title: 'Redeemed',
      cell: (cntx: any) => {
        const { quantityNotRedeemed, quantityRedeemed } = cntx.row.original;
        const payByUser = isPayByUser(deal.paymentArrangement);
        const normalizedQuantityNotRedeemed =
          getNormalizedCount(quantityNotRedeemed);
        const normalizedQuantityRedeemed = getNormalizedCount(quantityRedeemed);
        const total =
          normalizedQuantityRedeemed + normalizedQuantityNotRedeemed;
        const percentage = getClampedPercentage(
          total === 0 ? 0 : (normalizedQuantityRedeemed / total) * 100,
        );
        let remainingTotal = payByUser ? '∞' : quantityNotRedeemed;
        if (isDisabled) {
          remainingTotal = 0;
        }
        return (
          <ProgressBarRow
            isPbu={payByUser}
            value={quantityRedeemed}
            available={remainingTotal}
            percentage={percentage}
          />
        );
      },
      width: 15,
    },
    {
      name: COLUMN.totalCost,
      title: 'Total Spent',
      cell: (cntx: any) => {
        const product = cntx.row.original as ProductApiModel;
        // Pay-by-User deals resolve Total Spent from the deal products endpoint.
        const value = isTotalsPending
          ? PENDING_PLACEHOLDER
          : `$${formatCurrency(getProductSpend(product))}`;
        return <SimpleResponsiveCell label="Enrolled Total" value={value} />;
      },
      width: 10,
    },
    {
      name: COLUMN.totalSavings,
      title: 'Total Savings',
      cell: (cntx: any) => {
        const product = cntx.row.original as ProductApiModel;
        // Only rendered for Pay-by-User deals, which resolve savings from the
        // deal products endpoint; show a placeholder until that request settles.
        const value = isTotalsPending
          ? PENDING_PLACEHOLDER
          : `$${formatCurrency(getProductSavings(product))}`;
        return <SimpleResponsiveCell label="Total Savings" value={value} />;
      },
      width: 10,
    },
  ];
};
interface DealProductsTableProps {
  deal: AgreementApiModel;
}

// Columns hidden when payment arrangement is "Pay by User"
const HIDDEN_FOR_PAY_BY_USER: string[] = [
  COLUMN.priceBase,
  COLUMN.quantityTotal,
];

// Columns hidden when payment arrangement is "Pay on Consumption"
const HIDDEN_FOR_PAY_ON_CONSUMPTION: string[] = [
  COLUMN.discount,
  COLUMN.totalSavings,
];

// Filter table columns based on payment arrangement
function filterColumns<T extends { name?: string }>(
  columns: T[],
  paymentArrangement: string,
) {
  const columnsToHide = isPayByUser(paymentArrangement)
    ? HIDDEN_FOR_PAY_BY_USER
    : HIDDEN_FOR_PAY_ON_CONSUMPTION;

  return columns.filter((col) => !columnsToHide.includes(col.name));
}

export default function DealProductsTable({ deal }: DealProductsTableProps) {
  // RTK Query dedupes the underlying request, so resolving totals here rather
  // than via props keeps the component self-contained without extra fetches.
  const {
    isPending: isTotalsPending,
    getProductSpend,
    getProductSavings,
    totalSpend: dealTotalSpend,
    totalSavings: dealTotalSavings,
  } = useDealTotalSpend(
    deal.id,
    deal.paymentArrangement,
    deal.totalSpend,
    deal.totalSavings,
  );

  // Memoized so the `?? []` fallback does not produce a new array identity on
  // every render, which would defeat the memos that depend on it.
  const products = useMemo(() => deal.products ?? [], [deal.products]);

  const footerColumns = useMemo<FooterColumn[]>(
    () => [
      {
        name: COLUMN.name,
        cell: () => null,
      },
      {
        name: COLUMN.priceBase,
        cell: () => null,
      },
      {
        name: COLUMN.discount,
        cell: () => null,
      },
      {
        name: COLUMN.quantityTotal,
        cell: (data: ProductApiModel[]) => {
          let total = 0;
          data.forEach((val) => {
            total += getNetQuantity(val);
          });
          return <span className={styles.footerCell}>{total}</span>;
        },
      },
      {
        name: COLUMN.quantityAssigned,
        cell: (data: ProductApiModel[]) => {
          let total = 0;
          data.forEach((val) => {
            total += val.quantityAssigned;
          });
          return <span className={styles.footerCell}>{total}</span>;
        },
      },
      {
        name: COLUMN.quantityRedeemed,
        cell: (data: ProductApiModel[]) => {
          let total = 0;
          data.forEach((val) => {
            total += val.quantityRedeemed;
          });
          return <span className={styles.footerCell}>{total}</span>;
        },
      },
      {
        name: COLUMN.totalCost,
        cell: () => (
          <span className={styles.footerCell}>
            {isTotalsPending
              ? PENDING_PLACEHOLDER
              : `$${formatCurrency(dealTotalSpend)}`}
          </span>
        ),
      },
      {
        name: COLUMN.totalSavings,
        cell: () => (
          <span className={styles.footerCell}>
            {isTotalsPending
              ? PENDING_PLACEHOLDER
              : `$${formatCurrency(dealTotalSavings)}`}
          </span>
        ),
      },
    ],
    [isTotalsPending, dealTotalSpend, dealTotalSavings],
  );

  const tableColumns = useMemo(
    () =>
      getTableColumns(
        deal,
        getProductSpend,
        getProductSavings,
        isTotalsPending,
      ),
    // getTableColumns reads paymentArrangement, enabled and the derived
    // expiry/sold-out state in addition to products, so depend on the whole deal.
    [deal, getProductSpend, getProductSavings, isTotalsPending],
  );

  const visibleTableColumns = useMemo(
    () => filterColumns(tableColumns, deal.paymentArrangement),
    [tableColumns, deal.paymentArrangement],
  );

  const visibleFooterColumns = useMemo(
    () => filterColumns(footerColumns, deal.paymentArrangement),
    [footerColumns, deal.paymentArrangement],
  );

  const [sortedColumn, setSortedColumn] = useState<SortedColumn>(() =>
    visibleTableColumns.find((e) => e.name === 'name'),
  );

  const sortedProducts = useMemo(() => {
    if (products && sortedColumn) {
      const filteredData = products?.slice();
      const sortOrder = sortedColumn.direction === 'Asc' ? 1 : -1;
      switch (sortedColumn.name) {
        case 'name': {
          filteredData.sort((a, b) =>
            // eslint-disable-next-line no-nested-ternary
            a.name === b.name ? 0 : a.name > b.name ? sortOrder : -sortOrder,
          );
          break;
        }
        default: {
          // name sort default
          filteredData.sort((a, b) =>
            // eslint-disable-next-line no-nested-ternary
            a.name === b.name ? 0 : a.name > b.name ? sortOrder : -sortOrder,
          );
          break;
        }
      }
      return filteredData;
    }
    return products;
  }, [products, sortedColumn]);

  return (
    <ResponsiveTable
      data={sortedProducts}
      columns={visibleTableColumns}
      footerColumns={visibleFooterColumns}
      onSort={(column) => setSortedColumn(column)}
    />
  );
}
